import { useTranslations } from 'use-intl';
import { Caption, Field, Input, Stack, Textarea } from '@leanin/ui';

// Measured with the head renderer's own module, so the counter cannot
// disagree with what ships.
import {
  DESCRIPTION_MAX,
  TITLE_BRAND_SUFFIX,
  TITLE_MAX,
  collapseWhitespace,
  type SearchCopy,
} from '@/lib/seo-text';

// Storage bounds from the `seo_title` / `seo_description` CHECK constraints;
// they only stop the keystroke the API boundary would reject. `maxLength`
// counts UTF-16 code units, so it is slightly stricter than the boundary and
// the CHECK, which count characters.
const TITLE_LIMIT = 200;
const DESCRIPTION_LIMIT = 500;

// Past this the title no longer fits alongside the brand suffix, so
// composeTitle drops the suffix.
const TITLE_TARGET = TITLE_MAX - TITLE_BRAND_SUFFIX.length;

export type SeoMetaField = {
  readonly label: string;
  readonly help: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string | undefined;
};

type Props = {
  readonly title: SeoMetaField;
  readonly description: SeoMetaField;
  /**
   * What the page will actually emit for the form's current values, from the
   * same builder the head renderer calls (`resourceSearchCopy` /
   * `trackSearchCopy`). Shown even when a field is empty, because that is
   * when the fallback ships and an editor most needs to see it.
   */
  readonly preview: SearchCopy;
};

/**
 * The search-result title and snippet, each counted against its search
 * target rather than its storage bound. Over target is a warning, never a
 * save blocker: Google measures in pixels, and the head degrades by dropping
 * the suffix or cutting the snippet.
 */
export function SeoMetaFields({ title, description, preview }: Props) {
  const t = useTranslations('Common');

  return (
    <>
      <SeoField
        field={title}
        target={TITLE_TARGET}
        limit={TITLE_LIMIT}
        rendered={preview.title}
        overTargetMessage={t('widgets.seoMeta.titleOverTarget', {
          limit: TITLE_TARGET,
          renderLimit: TITLE_MAX,
          suffix: TITLE_BRAND_SUFFIX.trim(),
        })}
      />

      <SeoField
        field={description}
        multiline
        target={DESCRIPTION_MAX}
        limit={DESCRIPTION_LIMIT}
        rendered={preview.description}
        overTargetMessage={t('widgets.seoMeta.descriptionOverTarget', {
          limit: DESCRIPTION_MAX,
        })}
      />
    </>
  );
}

type SeoFieldProps = {
  readonly field: SeoMetaField;
  readonly multiline?: boolean;
  readonly target: number;
  /** The storage bound, enforced on the control as `maxLength`. */
  readonly limit: number;
  /** What the head emits for this field's current value. */
  readonly rendered: string;
  readonly overTargetMessage: string;
};

function SeoField({
  field,
  multiline = false,
  target,
  limit,
  rendered,
  overTargetMessage,
}: SeoFieldProps) {
  const t = useTranslations('Common');

  // Counted after collapsing whitespace, as the head counts, so a pasted
  // blank line costs here what it costs there. A whitespace-only value is
  // saved as null, so it counts as empty and previews the fallback.
  const counted = collapseWhitespace(field.value).length;
  const overTarget = counted > target;
  // Worth showing whenever the head emits something other than what was
  // typed: a fallback for an empty field, or a cut or suffixed value. A blank
  // field is not blank in the result, so that line says what replaces it.
  const showPreview = rendered !== field.value;
  const previewKey = counted === 0 ? 'widgets.seoMeta.fallback' : 'widgets.seoMeta.preview';

  const control = multiline ? (
    <Textarea
      rows={3}
      value={field.value}
      onChange={(e) => field.onChange(e.target.value)}
      maxLength={limit}
      invalid={field.error !== undefined}
    />
  ) : (
    <Input
      value={field.value}
      onChange={(e) => field.onChange(e.target.value)}
      maxLength={limit}
      invalid={field.error !== undefined}
    />
  );

  // Every line sits in the Field's description so all of it is read on
  // focus. The status region is mounted empty and holds only the threshold
  // sentence: a region announces changes after it is registered, so this
  // speaks on the crossing and stays quiet per keystroke.
  return (
    <Field
      label={field.label}
      description={
        <Stack gap="xs">
          <Caption>{field.help}</Caption>
          <Caption>{t('widgets.seoMeta.counter', { count: counted, limit: target })}</Caption>
          <Stack role="status" aria-live="polite">
            {overTarget ? <Caption>{overTargetMessage}</Caption> : null}
          </Stack>
          {showPreview ? <Caption>{t(previewKey, { text: rendered })}</Caption> : null}
        </Stack>
      }
      error={field.error}
    >
      {control}
    </Field>
  );
}
